import React, { useEffect, useCallback, useState } from "react";
import { ProductListView } from "../components/ProductList.viewer";
import GenericPageTitle from "modus/components/generic-page-title/GenericPageTitle";
import { Box, Button, Switch, FormControlLabel, makeStyles, Theme } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { filterProductsByText, ProductSearch } from "../components/product-search/ProductSearch";
import { IProduct } from "pages/home/consumer/service/ProductService";
import { AxiosResponse } from "axios";
import { AsyncActionState } from "legacy-components/form/action/AsyncAction";
import { FormattedMessage } from "react-intl";
import AddProductForm from "../components/AddProductForm";
import { useHistory } from "react-router";
import { ConsumerProductService } from "pages/home/consumer/service/ConsumerProductService";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import { useSelector } from "react-redux";
import Authorizer from "modus/components/authorizer/Authorizer";

const useStyles = makeStyles((theme: Theme) => ({
    filterIcon: {
        color: '#00548a',
        fontSize: '24px',
        marginRight: '8px'
    },
    searchBox: {
        transform: 'scale(1.15)',
        transformOrigin: 'left center'
    },
    formControlLabel: {
        marginRight: 16
    }
}));

export interface ProductDiscoveryProps {
    asyncResponse: AsyncActionState<AxiosResponse<IProduct[]> | null>| undefined
    isGuest?: boolean;
}

export const ProductDiscovery:React.FC<ProductDiscoveryProps> = ({asyncResponse, isGuest}) => {
    const classes = useStyles();
    const { goToProductDetails } = useProductNavigation();

    const [filteredProducts, setFilteredProducts] = React.useState<IProduct[]>();
    const [searchText, setSearchText] = React.useState<string>('');
    const [openAppCreate, setOpenAppCreate] = React.useState(false);
    const [forceRefresh, setForceRefresh] = useState(0); // Add a force refresh counter
    const identityEnvironment = useSelector((state: any) => state?.common?.identityEnvironment);

    // Create a local instance of the product list async action so we can reload it
    const {state: localResponse, subscribe: reloadProducts} = useAsyncAction(() =>
        ConsumerProductService.getProductList(identityEnvironment)
    );

    const [showPublisherProducts, setShowPublisherProducts] = useState(false);
    const [filteredPublisherProducts, setFilteredPublisherProducts] = useState<IProduct[]>();
    const { state: publisherResponse, subscribe: reloadPublisherProducts } = useAsyncAction(() =>
        ConsumerProductService.getProductListWithTeam(identityEnvironment, true)
    );

    const handleToggle = () => setShowPublisherProducts((prev) => !prev);

    useEffect(() => {
        reloadPublisherProducts(false);
    }, [identityEnvironment]);

    const filterPublisherProducts = useCallback((text?: string) => {
        const filterText = text !== undefined ? text : searchText;
        const responseData = publisherResponse?.value?.data || asyncResponse?.value?.data

        if (!responseData || responseData.length === 0) {
            setFilteredPublisherProducts([]);
            return;
        }

        let filtered = responseData as IProduct[];;
        if (filterText && filterText.trim() !== "") {
            filtered = filterProductsByText(filtered, filterText);
        }
        setFilteredPublisherProducts(filtered);
    }, [searchText, publisherResponse?.value?.data]);

    useEffect(() => {
        if (showPublisherProducts) {
            const responseData = publisherResponse?.value?.data;
            if (!responseData) return;
            if ((!filteredPublisherProducts || filteredPublisherProducts.length === 0) && responseData.length > 0) {
                setFilteredPublisherProducts(responseData as IProduct[]);
            } else {
                filterPublisherProducts(searchText);
            }
        }
    }, [showPublisherProducts, publisherResponse?.value?.data, searchText, filterPublisherProducts]);


    const handleOpenAddProduct = () => setOpenAppCreate(true);
    const handleCloseAddProduct = () => setOpenAppCreate(false);

    const handleAddProduct = useCallback(async (product: any, shouldNavigate?: boolean) => {
        if (shouldNavigate && product && product.name) {
          goToProductDetails(product.id);
        } else if (reloadProducts) {
          try {
            setFilteredProducts(undefined);
            setForceRefresh((prev) => prev + 1);
            reloadProducts(false);
          } catch (error) {
            reloadProducts(false);
          }
        }
      }, [goToProductDetails, reloadProducts, setFilteredProducts, setForceRefresh]);


 function useProductNavigation() {
  const history = useHistory();
  return {
    goToProductDetails: (productName: string) =>
      history.push(`/home/consumer/products/${encodeURIComponent(productName)}`)
  };
}

const filterProducts = (searchText?: string) => {
        if(asyncResponse?.value?.data && asyncResponse?.value?.data?.length > 0) {
            if(!searchText || searchText?.trim() === "") {
                setFilteredProducts(asyncResponse?.value?.data);
                return;
            }
            const filteredProducts = filterProductsByText(asyncResponse?.value?.data, searchText);
            setFilteredProducts(filteredProducts);
        }
    }

    useEffect(() => {
        if(!asyncResponse?.value?.data) {
            return;
        }
        filterProducts("");
    },[asyncResponse?.value?.data]);
    
    return (
        <Box maxWidth="100%" overflow="hidden">    
            <GenericPageTitle
                title={"Products"}
                description={<>Subscribe to products to begin leveraging API and Event capabilities.</>}
                controls={
                    <Box display={"flex"} flexWrap="wrap">
                        <Box ml="15px" />
                        <Authorizer resource="api_products" action="create">
                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleOpenAddProduct}
                                variant="contained"
                                color="primary"
                            >
                                <FormattedMessage defaultMessage="Add New Product" />
                            </Button>
                        
                        <AddProductForm
                            open={openAppCreate}
                            onClose={handleCloseAddProduct}
                            onSubmit={handleAddProduct}
                        />
                        </Authorizer>
                    </Box>
                }
                noSkeleton
            />
            {/* Search and Filter UI */}
            <Box display={"flex"} mb="20px" mt="20px" paddingRight="10px" flexGrow={2}>
              <Box display="flex" alignItems="center">
                <i className={`modus-icons ${classes.filterIcon}`} aria-hidden="true">filter</i>
                <Box className={classes.searchBox}>
                  <ProductSearch onSearch={(text) => {(showPublisherProducts) ? filterPublisherProducts(text) : filterProducts(text) }} />
                </Box>
              </Box>

                <Box marginLeft={5}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showPublisherProducts}
                                onChange={handleToggle}
                                color="primary"
                            />
                        }
                        labelPlacement= "start"
                        label={"Show My Team Products"}
                        className={classes.formControlLabel}
                    />
                </Box>

            </Box>




            <Box width="100%" overflow="hidden">
                {showPublisherProducts ?
                    <ProductListView
                        asyncResponse={{
                            ...asyncResponse,
                            ...localResponse,
                            value: {
                                data: Array.isArray(filteredPublisherProducts) ? filteredPublisherProducts :
                                    (publisherResponse?.value?.data || asyncResponse?.value?.data)
                            }
                        } as any}
                        filteredProducts={filteredPublisherProducts}
                        isGuest={isGuest}
                    />
                    :
                    <ProductListView
                        asyncResponse={{
                            ...asyncResponse,
                            ...localResponse,
                            value: {
                                data: Array.isArray(filteredProducts) ? filteredProducts :
                                    (localResponse?.value?.data || asyncResponse?.value?.data)
                            }
                        } as any}
                        filteredProducts={filteredProducts}
                        isGuest={isGuest}
                    /> }
            </Box>
        </Box>
    )
};

export default ProductDiscovery;